import { BUSINESS_TYPES, TAX_REGIMES, totpEnableSchema, updateTenantSchema, uploadRequestSchema, type ModuleKey } from '@alicerce/contracts';
import { Alert, Badge, Button, Card, CardBody, CardHeader, Dialog, Field, formatDateTime, PageHeader, PageLoader, Select, Table, Td, TextField, Th, Tr, Tabs } from '@alicerce/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { QRCodeSVG } from 'qrcode.react';
import { useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { FilePicker, useUpload } from '@/components/upload';
import { errorMessage } from '@/lib/api';
import { useZodForm } from '@/lib/form';
import { useApi, useCurrent, useSession } from '@/lib/session';
import { BUSINESS_TYPE_LABEL, TAX_REGIME_LABEL } from '../onboarding/create-tenant';

export interface NotificationPreference {
  panel: boolean;
  email: boolean;
  whatsapp: boolean;
}

export interface TenantView {
  id: string;
  tradeName: string;
  legalName: string | null;
  document: string | null;
  businessType: (typeof BUSINESS_TYPES)[number];
  taxRegime: (typeof TAX_REGIMES)[number] | null;
  slug: string;
  logoUrl: string | null;
  settings: {
    logoKey?: string;
    primaryColor?: string;
    secondaryColor?: string;
    ownerWhatsapp?: string;
    accountantEmail?: string;
    notifications?: Record<string, NotificationPreference>;
  } | null;
}

const TABS = [
  { key: 'negocio', label: 'Negócio' },
  { key: 'notificacoes', label: 'Notificações' },
  { key: 'seguranca', label: 'Segurança' },
  { key: 'suporte', label: 'Acessos do suporte' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

export function useTenant() {
  const api = useApi();
  return useQuery({ queryKey: ['tenant'], queryFn: () => api.get<TenantView>('/v1/core/tenant') });
}

export function SettingsLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const active = (TABS.find((t) => pathname.startsWith(`/configuracoes/${t.key}`))?.key ?? 'negocio') as TabKey;
  return (
    <div className="space-y-6">
      <PageHeader title="Configurações" description="Dados do negócio, avisos e segurança da sua conta." />
      <Tabs items={[...TABS]} value={active} onChange={(k) => navigate(`/configuracoes/${k}`)} />
      <Outlet />
    </div>
  );
}

export function SettingsIndex() {
  return <Navigate to="/configuracoes/negocio" replace />;
}

// ---------- Negócio ----------

export function BusinessSettingsPage() {
  const q = useTenant();
  if (q.isPending) return <PageLoader />;
  if (q.isError) return <Alert tone="danger">{errorMessage(q.error)}</Alert>;
  return (
    <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
      <BusinessForm key={q.data.id} tenant={q.data} />
      <LogoCard tenant={q.data} />
    </div>
  );
}

interface BusinessValues {
  tradeName: string;
  legalName: string;
  document: string;
  businessType: TenantView['businessType'];
  taxRegime: string;
  settings: { primaryColor: string; secondaryColor: string; ownerWhatsapp: string; accountantEmail: string };
  [key: string]: unknown;
}

/** Campos vazios do formulário viram "não informado" antes de validar com o schema da API. */
const businessFormSchema = z.preprocess((raw) => {
  const v = raw as BusinessValues;
  return {
    tradeName: v.tradeName,
    legalName: v.legalName || null,
    document: v.document.replace(/\D/g, '') || null,
    businessType: v.businessType,
    taxRegime: v.taxRegime || null,
    settings: Object.fromEntries(Object.entries(v.settings).filter(([, value]) => value !== '')),
  };
}, updateTenantSchema);

function BusinessForm({ tenant }: { tenant: TenantView }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { refreshMe } = useSession();
  const { can } = useCurrent();
  const editable = can('tenant:update');
  const [saved, setSaved] = useState(false);
  const form = useZodForm<typeof businessFormSchema, BusinessValues>(businessFormSchema, {
    tradeName: tenant.tradeName,
    legalName: tenant.legalName ?? '',
    document: tenant.document ?? '',
    businessType: tenant.businessType,
    taxRegime: tenant.taxRegime ?? '',
    settings: {
      primaryColor: tenant.settings?.primaryColor ?? '#2563eb',
      secondaryColor: tenant.settings?.secondaryColor ?? '#0f172a',
      ownerWhatsapp: tenant.settings?.ownerWhatsapp ?? '',
      accountantEmail: tenant.settings?.accountantEmail ?? '',
    },
  });
  const v = form.values;
  const setSetting = (key: keyof typeof v.settings, value: string) => form.set('settings', { ...v.settings, [key]: value });

  const onSubmit = form.submit(async (body) => {
    setSaved(false);
    const updated = await api.patch<TenantView>('/v1/core/tenant', body);
    queryClient.setQueryData(['tenant'], updated);
    await refreshMe();
    setSaved(true);
  });

  return (
    <Card>
      <CardHeader title="Dados do negócio" description="Aparecem na loja, nas notas e nas cobranças." />
      <CardBody>
        <form className="space-y-4" onSubmit={onSubmit} noValidate>
          {form.formError && <Alert tone="danger">{form.formError}</Alert>}
          {saved && <Alert tone="success">Dados salvos.</Alert>}
          <fieldset disabled={!editable} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField label="Nome fantasia" value={v.tradeName} error={form.errors.tradeName} onChange={(e) => form.set('tradeName', e.target.value)} />
              <TextField label="Razão social" value={v.legalName} error={form.errors.legalName} onChange={(e) => form.set('legalName', e.target.value)} />
              <TextField label="CPF ou CNPJ" inputMode="numeric" value={v.document} error={form.errors.document} onChange={(e) => form.set('document', e.target.value)} />
              <Field label="O que você vende" error={form.errors.businessType}>
                {(a11y) => (
                  <Select {...a11y} value={v.businessType} onChange={(e) => form.set('businessType', e.target.value as TenantView['businessType'])}>
                    {BUSINESS_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {BUSINESS_TYPE_LABEL[t]}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label="Regime tributário" error={form.errors.taxRegime}>
                {(a11y) => (
                  <Select {...a11y} value={v.taxRegime} onChange={(e) => form.set('taxRegime', e.target.value)}>
                    <option value="">Não sei / não informado</option>
                    {TAX_REGIMES.map((t) => (
                      <option key={t} value={t}>
                        {TAX_REGIME_LABEL[t]}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <TextField
                label="WhatsApp do dono"
                type="tel"
                hint="Para avisos importantes, se você ligar o WhatsApp nas notificações."
                value={v.settings.ownerWhatsapp}
                error={form.errors['settings.ownerWhatsapp']}
                onChange={(e) => setSetting('ownerWhatsapp', e.target.value)}
              />
              <TextField
                label="E-mail do contador"
                type="email"
                hint="Recebe as exportações de notas e o fechamento do mês."
                value={v.settings.accountantEmail}
                error={form.errors['settings.accountantEmail']}
                onChange={(e) => setSetting('accountantEmail', e.target.value)}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <ColorField label="Cor principal" value={v.settings.primaryColor} error={form.errors['settings.primaryColor']} onChange={(c) => setSetting('primaryColor', c)} />
              <ColorField label="Cor secundária" value={v.settings.secondaryColor} error={form.errors['settings.secondaryColor']} onChange={(c) => setSetting('secondaryColor', c)} />
            </div>
          </fieldset>
          {editable && (
            <Button type="submit" loading={form.submitting}>
              Salvar
            </Button>
          )}
        </form>
      </CardBody>
    </Card>
  );
}

function ColorField({ label, value, error, onChange }: { label: string; value: string; error?: string; onChange: (v: string) => void }) {
  return (
    <Field label={label} error={error}>
      {(a11y) => (
        <div className="flex items-center gap-2">
          <input type="color" aria-label={`${label} (seletor)`} className="h-10 w-12 cursor-pointer rounded border border-line" value={/^#[0-9a-f]{6}$/i.test(value) ? value : '#000000'} onChange={(e) => onChange(e.target.value)} />
          <input {...a11y} className="h-10 w-32 rounded-lg border border-line px-3 font-mono text-sm" value={value} onChange={(e) => onChange(e.target.value)} />
        </div>
      )}
    </Field>
  );
}

function LogoCard({ tenant }: { tenant: TenantView }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { can } = useCurrent();
  const { upload, busy, error } = useUpload(
    (file) => api.post('/v1/core/tenant/logo-upload-url', uploadRequestSchema.parse({ contentType: file.type, size: file.size })),
    { maxBytes: 10 * 1024 * 1024, accept: (t) => /^image\/(png|jpeg|webp|gif|avif)$/.test(t), typeMessage: 'Envie uma imagem (PNG, JPG, WebP, GIF ou AVIF).' },
  );
  const save = useMutation({
    mutationFn: (logoKey: string) => api.patch<TenantView>('/v1/core/tenant', { settings: { logoKey } }),
    onSuccess: (t) => queryClient.setQueryData(['tenant'], t),
  });
  return (
    <Card>
      <CardHeader title="Logo" description="Usado na loja, nos e-mails e no painel." />
      <CardBody className="space-y-3">
        {tenant.logoUrl ? (
          <img src={tenant.logoUrl} alt={`Logo de ${tenant.tradeName}`} className="mx-auto max-h-28 object-contain" />
        ) : (
          <p className="text-sm text-muted">Nenhum logo enviado.</p>
        )}
        {(error || save.error) && <Alert tone="danger">{error ?? errorMessage(save.error)}</Alert>}
        {can('tenant:update') && (
          <FilePicker
            accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
            busy={busy || save.isPending}
            onFiles={async ([file]) => {
              if (!file) return;
              const key = await upload(file);
              if (key) save.mutate(key);
            }}
          />
        )}
      </CardBody>
    </Card>
  );
}

// ---------- Notificações ----------

/** Avisos que a API envia, agrupados pelo módulo que os gera. A chave é o `type` da notificação. */
export const NOTIFICATION_TYPES: { module: ModuleKey; group: string; items: { type: string; label: string }[] }[] = [
  {
    module: 'core',
    group: 'Geral',
    items: [{ type: 'core.import_finished', label: 'Importação de produtos concluída' }],
  },
  {
    module: 'billing',
    group: 'Assinatura',
    items: [
      { type: 'billing.payment_failed', label: 'Pagamento da assinatura falhou' },
      { type: 'billing.usage_threshold', label: 'Consumo perto do limite do plano' },
      { type: 'billing.trial_ended', label: 'Fim do período de teste' },
      { type: 'billing.read_only', label: 'Conta em modo somente leitura' },
      { type: 'billing.canceled', label: 'Assinatura cancelada' },
      { type: 'billing.credits_added', label: 'Créditos adicionados' },
    ],
  },
  {
    module: 'checkout',
    group: 'Pedidos e pagamentos',
    items: [
      { type: 'checkout.order_paid', label: 'Pedido pago' },
      { type: 'checkout.order_manual_payment', label: 'Pedido aguardando confirmação manual' },
      { type: 'checkout.kyc_changed', label: 'Situação da conta de recebimento mudou' },
      { type: 'checkout.payout_changed', label: 'Saque enviado, com falha ou destino alterado' },
    ],
  },
  {
    module: 'inventory',
    group: 'Estoque',
    items: [
      { type: 'inventory.low_stock', label: 'Produto abaixo do estoque mínimo' },
      { type: 'inventory.sale_without_stock', label: 'Venda sem estoque' },
      { type: 'inventory.reservation_failed', label: 'Falha ao reservar estoque' },
    ],
  },
  {
    module: 'finance',
    group: 'Gastos e lucros',
    items: [{ type: 'finance.mei_limit', label: 'Faturamento perto do limite do MEI' }],
  },
  {
    module: 'fiscal',
    group: 'Notas fiscais',
    items: [
      { type: 'fiscal.document_rejected', label: 'Nota rejeitada' },
      { type: 'fiscal.cancel_suggested', label: 'Sugestão de cancelar nota' },
      { type: 'fiscal.certificate_expiring', label: 'Certificado digital vencendo' },
    ],
  },
  {
    module: 'storefront',
    group: 'Loja online',
    items: [
      { type: 'storefront.domain_active', label: 'Domínio próprio ativo' },
      { type: 'storefront.booking_created', label: 'Novo agendamento pela loja' },
      { type: 'storefront.booking_canceled', label: 'Agendamento cancelado pelo cliente' },
    ],
  },
  {
    module: 'ads',
    group: 'Anúncios',
    items: [
      { type: 'ads.campaign_rejected', label: 'Campanha reprovada' },
      { type: 'ads.campaign_returned', label: 'Campanha devolvida para ajustes' },
    ],
  },
];

const DEFAULT_PREF: NotificationPreference = { panel: true, email: true, whatsapp: false };
const CHANNELS: { key: keyof NotificationPreference; label: string }[] = [
  { key: 'panel', label: 'Painel' },
  { key: 'email', label: 'E-mail' },
  { key: 'whatsapp', label: 'WhatsApp' },
];

export function NotificationSettingsPage() {
  const q = useTenant();
  if (q.isPending) return <PageLoader />;
  if (q.isError) return <Alert tone="danger">{errorMessage(q.error)}</Alert>;
  return <NotificationPreferences key={q.data.id} tenant={q.data} />;
}

function NotificationPreferences({ tenant }: { tenant: TenantView }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { can, isModuleActive } = useCurrent();
  const editable = can('tenant:update');
  const [prefs, setPrefs] = useState<Record<string, NotificationPreference>>(tenant.settings?.notifications ?? {});
  const save = useMutation({
    mutationFn: () => api.patch<TenantView>('/v1/core/tenant', { settings: { notifications: prefs } }),
    onSuccess: (t) => queryClient.setQueryData(['tenant'], t),
  });
  const groups = NOTIFICATION_TYPES.filter((g) => isModuleActive(g.module));
  const toggle = (type: string, channel: keyof NotificationPreference, on: boolean) => {
    save.reset();
    setPrefs((p) => ({ ...p, [type]: { ...DEFAULT_PREF, ...p[type], [channel]: on } }));
  };

  return (
    <Card>
      <CardHeader
        title="Como você quer ser avisado"
        description={tenant.settings?.ownerWhatsapp ? 'O WhatsApp vai para o número do dono.' : 'Para receber pelo WhatsApp, informe o número do dono na aba Negócio.'}
        actions={
          editable && (
            <Button loading={save.isPending} onClick={() => save.mutate()}>
              Salvar
            </Button>
          )
        }
      />
      {save.isSuccess && (
        <div className="px-5 pt-4">
          <Alert tone="success">Preferências salvas.</Alert>
        </div>
      )}
      {save.isError && (
        <div className="px-5 pt-4">
          <Alert tone="danger">{errorMessage(save.error)}</Alert>
        </div>
      )}
      <Table>
        <thead>
          <tr>
            <Th>Aviso</Th>
            {CHANNELS.map((c) => (
              <Th key={c.key} className="text-center">
                {c.label}
              </Th>
            ))}
          </tr>
        </thead>
        {groups.map((g) => (
          <tbody key={g.module}>
            <tr>
              <th colSpan={4} scope="colgroup" className="bg-canvas px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted">
                {g.group}
              </th>
            </tr>
            {g.items.map((item) => {
              const pref = { ...DEFAULT_PREF, ...prefs[item.type] };
              return (
                <Tr key={item.type}>
                  <Td>{item.label}</Td>
                  {CHANNELS.map((c) => (
                    <Td key={c.key} className="text-center">
                      <input
                        type="checkbox"
                        className="size-4 accent-brand-600"
                        aria-label={`${item.label}: ${c.label}`}
                        checked={pref[c.key]}
                        disabled={!editable}
                        onChange={(e) => toggle(item.type, c.key, e.target.checked)}
                      />
                    </Td>
                  ))}
                </Tr>
              );
            })}
          </tbody>
        ))}
      </Table>
    </Card>
  );
}

// ---------- Segurança ----------

export function SecuritySettingsPage() {
  const api = useApi();
  const { me, refreshMe } = useSession();
  const [dialog, setDialog] = useState<'enable' | 'disable' | null>(null);
  const resend = useMutation({ mutationFn: () => api.post('/v1/auth/verify-email/resend', undefined, { noTenant: true }) });
  if (!me) return <PageLoader />;
  const user = me.user;
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card>
        <CardHeader title="E-mail" />
        <CardBody className="space-y-3 text-sm">
          <p>
            {user.email} {user.emailVerifiedAt ? <Badge tone="success">confirmado</Badge> : <Badge tone="warning">não confirmado</Badge>}
          </p>
          {!user.emailVerifiedAt &&
            (resend.isSuccess ? (
              <Alert tone="success">Enviamos um novo link. Confira sua caixa de entrada.</Alert>
            ) : (
              <>
                {resend.isError && <Alert tone="danger">{errorMessage(resend.error)}</Alert>}
                <Button variant="secondary" size="sm" loading={resend.isPending} onClick={() => resend.mutate()}>
                  Reenviar link de confirmação
                </Button>
              </>
            ))}
        </CardBody>
      </Card>
      <Card>
        <CardHeader title="Verificação em duas etapas" description="Pede um código do app autenticador (Google Authenticator, Authy) ao entrar." />
        <CardBody className="space-y-3 text-sm">
          {user.twoFactorEnabled ? (
            <>
              <p>
                <Badge tone="success">ativada</Badge>
              </p>
              <Button variant="secondary" size="sm" onClick={() => setDialog('disable')}>
                Desativar
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={() => setDialog('enable')}>
              Ativar
            </Button>
          )}
        </CardBody>
      </Card>
      {dialog === 'enable' && <EnableTotpDialog onClose={() => setDialog(null)} onDone={refreshMe} />}
      {dialog === 'disable' && <DisableTotpDialog onClose={() => setDialog(null)} onDone={refreshMe} />}
    </div>
  );
}

function EnableTotpDialog({ onClose, onDone }: { onClose: () => void; onDone: () => Promise<unknown> }) {
  const api = useApi();
  const setup = useQuery({
    queryKey: ['2fa-setup'],
    queryFn: () => api.post<{ secret: string; otpauthUrl: string }>('/v1/auth/2fa/setup', undefined, { noTenant: true }),
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
  });
  const form = useZodForm(totpEnableSchema, { code: '' });
  const onSubmit = form.submit(async (data) => {
    await api.post('/v1/auth/2fa/enable', data, { noTenant: true });
    await onDone();
    onClose();
  });
  return (
    <Dialog open onClose={onClose} title="Ativar verificação em duas etapas">
      {setup.isPending ? (
        <PageLoader />
      ) : setup.isError ? (
        <Alert tone="danger">{errorMessage(setup.error)}</Alert>
      ) : (
        <form className="space-y-4" onSubmit={onSubmit} noValidate>
          <p className="text-sm">1. Leia o código com o app autenticador.</p>
          <div className="flex justify-center rounded-lg bg-white p-3">
            <QRCodeSVG value={setup.data.otpauthUrl} size={176} title="QR code para o app autenticador" />
          </div>
          <p className="text-xs text-muted">
            Sem câmera? Digite a chave <code className="break-all font-mono">{setup.data.secret}</code>
          </p>
          <p className="text-sm">2. Digite o código de 6 dígitos que aparece no app.</p>
          {form.formError && <Alert tone="danger">{form.formError}</Alert>}
          <TextField label="Código" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={form.values.code} error={form.errors.code} onChange={(e) => form.set('code', e.target.value.replace(/\D/g, ''))} />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" loading={form.submitting}>
              Ativar
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}

function DisableTotpDialog({ onClose, onDone }: { onClose: () => void; onDone: () => Promise<unknown> }) {
  const api = useApi();
  const form = useZodForm(totpEnableSchema, { code: '' });
  const onSubmit = form.submit(async (data) => {
    await api.post('/v1/auth/2fa/disable', data, { noTenant: true });
    await onDone();
    onClose();
  });
  return (
    <Dialog open onClose={onClose} title="Desativar verificação em duas etapas" description="Confirme com um código do app autenticador.">
      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        {form.formError && <Alert tone="danger">{form.formError}</Alert>}
        <TextField label="Código" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={form.values.code} error={form.errors.code} onChange={(e) => form.set('code', e.target.value.replace(/\D/g, ''))} />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" variant="danger" loading={form.submitting}>
            Desativar
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ---------- Acessos do suporte ----------

export function SupportAccessPage() {
  const api = useApi();
  const q = useQuery({
    queryKey: ['support-access'],
    queryFn: () => api.get<{ at: string; action: string; metadata: { reason?: string } | null }[]>('/v1/core/tenant/support-access'),
  });
  return (
    <Card>
      <CardHeader title="Acessos da equipe Gestora" description="Toda vez que o suporte entra na sua conta para ajudar, fica registrado aqui. O acesso é só de leitura." />
      {q.isPending ? (
        <PageLoader />
      ) : q.isError ? (
        <CardBody>
          <Alert tone="danger">{errorMessage(q.error)}</Alert>
        </CardBody>
      ) : q.data.length === 0 ? (
        <CardBody>
          <p className="text-sm text-muted">Nenhum acesso do suporte até agora.</p>
        </CardBody>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Quando</Th>
              <Th>Motivo</Th>
            </tr>
          </thead>
          <tbody>
            {q.data.map((r) => (
              <Tr key={r.at}>
                <Td className="whitespace-nowrap">{formatDateTime(r.at)}</Td>
                <Td>{r.metadata?.reason ?? '—'}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

